import * as THREE from "three";
import { SNAG_TIME, SNAG_MASH, SNAG_CREEP, SNAG_IMMUNE } from "@catworld/shared";
import { SNAG_BOB, SNAG_BOB_DOWN } from "./burrs.js";

const GRAVITY = 15.5;
const JUMP_HEIGHT = 0.95;          // metres of apex above the take-off surface
const WALK_TOP = 0.62;             // m/s without a modifier
const STALK_TOP = 0.26;            // m/s crouched -- a stalking cat creeps
const RUN_TOP = 2.40;              // m/s holding sprint
const GAIT_SWAP = 0.86;            // m/s where the walk clip hands over to run
const SWIPE_TIME = 1.55;           // one-shot claw swipe, matches the clip
const ACCEL = 14;
const AIR_ACCEL = 5.5;
const FRICTION = 16;
const TURN_RATE = 11;
const COYOTE = 0.11;               // grace after walking off an edge
const BUFFER = 0.14;               // grace for releasing jump before touchdown

/**
 * The pounce.  Jump is release-triggered: a hold gathers a charge while the
 * cat is grounded, and the release launches with the apex scaled by how long
 * the crouch was held.  A tap -- press one frame, release the next --
 * accumulates nothing and launches at power 1.0, so it is exactly the old
 * jump one frame late; walk.mjs's stall pulses ride on that.
 */
const CHARGE_TIME = 0.62;  // hold for a full charge.  A full pounce is a
                           // decision, not a reflex: past POSE_DELAY the
                           // crouch has half a second to read before it peaks
const POWER_MAX = 1.5;     // apex multiplier at full charge -- 1.42 m up from
                           // 0.95.  reach.mjs floods with the uncharged
                           // MOVE.jumpHeight, so no route may *require* a
                           // pounce; the extra height buys shortcuts only
const CHARGE_TOP = 0.22;   // m/s while gathering, under even the 0.26 stalk:
                           // legs coiled for the launch are not for walking
const POSE_DELAY = 0.09;   // s of hold before the crouch (and the CHARGE_TOP
                           // cap that arrives with it) shows.  A human tap is
                           // 40-80 ms of key-down, and below this line a hold
                           // is still presumed a tap and stays a plain jump
const POUNCE_LUNGE = 2.6;  // m/s of forward launch at full charge.  Gathering
                           // costs the run-up, so without this a full pounce
                           // measured 0.45 m of travel against a plain sprint
                           // jump's 1.68 -- charging made the cat leap LESS
                           // far.  At 2.6 the same pounce carries ~2.3 m in
                           // its 0.83 s of air, which beats the sprint jump
                           // by the same sort of margin the height does
const AIM_GRACE = 0.13;    // s a released direction still counts as aim.  The
                           // lunge is spent only when the player is steering,
                           // and "steering" used to mean the single frame of
                           // input the release landed on: let W up two frames
                           // before Space and a full charge fired straight up
                           // instead of across the gap it was pointed at.
                           // Same family as COYOTE and BUFFER -- forgive the
                           // edge of an input, never the input itself -- and
                           // it is what lets the aim preview promise an arc
                           // the very next frame's release still honours.
                           // Longer than a hand's worth of key-release skew
                           // (~50 ms), shorter than any deliberate "let go to
                           // pounce straight up", which costs a beat anyway

/**
 * The claws.  Holding left mouse puts them out; an airborne cat pressed
 * against a *climbable* face (collider.climb, tagged by level/builder.js --
 * wood, and nothing else) hooks on and hangs there: gravity suspended,
 * position held, until the claws come up, Space kicks it off, or it scrabbles
 * onto footing.  Space from a cling is a plain uncharged jump straight up the
 * face -- a pounce is gathered against the ground with the hind legs planted,
 * and a cat hanging by its forepaws has nothing to gather with, so the wall
 * jump is press-triggered and always power 1.0.  That is also what makes the
 * ratchet work: cling, tap, re-cling ~0.9 m higher, which clears the 1.81 m
 * fences in two taps without ever handing out more than JUMP_HEIGHT per leap.
 */
const CLING_CATCH = 0.6;   // m/s: claws catch only once the rise has slowed
                           // to this -- the last v^2/2g = 12 mm of the arc --
                           // or on the way down.  Without the gate a wall
                           // jump re-hooks on its own first frame (it leaves
                           // at 5.4 m/s beside the very face it came from)
                           // and the ratchet cancels itself
const CLING_CLIMB = 0.40;  // m/s of scrabble along the face, between the
                           // 0.26 stalk and the 0.62 walk: fence base to the
                           // top hold is ~3 s of deliberate clawing, so the
                           // wall jump stays the fast way up
const CLAW_HOOK = 0.28;    // where on the 0.32 m body the front claws hook.
                           // The wall must reach this far above the paws or
                           // there is nothing to hang from -- which is also
                           // what parks the highest hold 0.28 m below a
                           // fence cap and leaves the last leg to the jump

/**
 * The landing assist: cat-like agility for narrow tops.  A 0.16 m fence rail
 * under a 0.24 m-wide cat leaves a landing lane 0.40 m wide, and arriving
 * from a jump arc the miss is usually a few centimetres of drift -- so while
 * the cat is FALLING past the top of something narrow it gets nudged
 * sideways toward that top.  Strictly bounded: horizontal only, only below
 * ASSIST_PULL per second, only when the miss is within ASSIST_MARGIN of
 * making it anyway, and only toward a surface already beneath the paws --
 * it can place the cat, never lift it and never fetch it.
 */
const ASSIST_TOP = 0.20;    // plan thickness at or under this is "narrow":
                            // fence runs are 0.16 and gate posts 0.13, while
                            // the thinnest thing meant to be simply walked on
                            // (a street tree's trunk collider) is 0.27
const ASSIST_ABOVE = 0.35;  // the window above the top where the nudge acts
const ASSIST_MARGIN = 0.10; // how far past the capsule's own catch it reaches
const ASSIST_PULL = 0.6;    // m/s of nudge.  Falling through the 0.35 window
                            // at a typical 2-3 m/s arrival leaves 0.12-0.17 s
                            // in it, so the pull can close about the largest
                            // gap it accepts and no more -- and the harder
                            // the arrival, the less it can do

/**
 * The burr tangle.
 *
 * A grounded cat that steps into a burred bush (game/src/burrs.js knows
 * where they are; `this.burrs` is the injected snare) is TANGLED: for up to
 * SNAG_TIME its ground speed is clamped to SNAG_CREEP, it cannot gather or
 * launch a jump, and it wriggles -- each fresh press of jump tears
 * SNAG_MASH off the clock, so mashing is the way out and waiting is the
 * ceiling.  All four numbers live in shared/constants.js because this same
 * code runs the AI cats on the server (world.js drives one Player per cat,
 * whoever is driving), and the tuning is a promise made to both ends.
 *
 * Three properties are load-bearing, in this order:
 *
 *   - IT ALWAYS ENDS.  snagFor is decremented unconditionally every frame
 *     it is positive; nothing pauses it, nothing extends it, and escape
 *     grants SNAG_IMMUNE seconds in which the snare is simply not consulted
 *     -- so a brain holding a straight course through a bush snags once and
 *     walks out the far side, and the soak's stillness gate never meets a
 *     cat this can strand.
 *   - IT ONLY EVER SUBTRACTS.  The tangle makes the cat move less and jump
 *     not at all; it adds no velocity, no teleport, no state a client could
 *     abuse.  That is why the effect may live client-side at all: the
 *     server's pose clamp refuses cats that move too much, never too
 *     little, and the happiness sting is judged server-side from the same
 *     bush list regardless of anything this file does (server/src/burrs.js).
 *   - IT SELF-HEALS ACROSS TELEPORTS.  Every tangled frame re-asks the
 *     snare with SNAG_GRIP of slack; a respawn or a seat-take that lands
 *     the cat bushless releases it at once, while the wriggle's own creep
 *     (0.28 m over a full passive tangle) can never reach the widened rim
 *     from anywhere inside the true one.
 */
const SNAG_GRIP = 0.35;    // slack, metres, on "still in the bush I'm in?"
                           // -- wide enough that honest creep and the pose
                           // jitter of a wriggle stay held, narrow enough
                           // that any real displacement (respawn is 40 m)
                           // is an instant release

/**
 * The cat's collision shape: a capsule stood on end.  In plan it is a stadium
 * -- a spine HALF_LEN fore and aft of the origin along `shapeYaw`, RADIUS to
 * either side -- swept straight up HEIGHT from the paws.
 *
 * It replaces an axis-aligned 0.24 m square box, which the rendered cats
 * overhang by a lot: measured in-browser they run 0.45-0.66 m nose to tail
 * with the nose 0.22-0.31 m ahead of the origin, so against any wall the head
 * carried 0.10-0.19 m into the geometry.  The fix has to *rotate with the
 * cat*, and a capsule is the right rotating shape here rather than an
 * oriented box: the axis-at-a-time resolver below stays closed-form (a circle
 * against an axis-aligned face is one square root), and a capsule's rounded
 * ends slide around corners and doorjambs that an OBB's corners would catch
 * on and convert into sudden sideways ejections.
 *
 * RADIUS keeps the old half-width exactly, on purpose.  Every clearance in
 * the level was tuned to a 0.24 m cat -- reach.mjs floods with
 * MOVE.halfWidth, the east bin lane pinches to ~0.27 m, the stepping stones
 * gap by 0.12 -- and sideways clearance is what those tunings actually
 * constrain, so it is not negotiable.
 *
 * HALF_LEN is a tradeoff, not a fit to the model.  Matching the full 0.66 m
 * cat would wedge it in every passage the moment it turned; staying at the
 * old square fixes nothing.  At 0.07 the plan footprint is 0.24 x 0.38 and
 * the nose plane sits 0.19 m out, which against the two measured offenders
 * (the brick underbuild's south face and the patio boulder) cuts the head
 * penetration from 0.10-0.19 m to 0.03-0.12 -- gone for the median cat,
 * a whisker-depth graze for the longest -- while the shape still turns
 * freely anywhere 0.38 m wide, which is everywhere except the bin pinch.
 */
const RADIUS = 0.12;
const HALF_LEN = 0.07;
const HEIGHT = 0.32;

/**
 * The stadium is tested as five circles strung along the spine.  A true
 * stadium-vs-box test is exact too, but resolving it along one axis at a time
 * means intersecting a slanted segment with a rounded rectangle and picking
 * the extreme over a piecewise arc -- a page of cases begging for a sign
 * error.  Circles keep every answer one clamp and one square root.  The cost
 * is a scallop between beads where the flank sags below the true stadium: at
 * this spacing (HALF_LEN/2 apart) it is R - sqrt(R^2 - (HALF_LEN/4)^2) = 1.3
 * mm, invisible next to the 30 mm of slack in the level's tightest lane.
 */
/**
 * One movement input, as a magnitude in [0, 1].
 *
 * Total, like the wire parsers: a key's 1, a stick's 0.42, an undefined field
 * on a harness-built input object and a NaN from arithmetic on one all resolve
 * to a number this file can multiply by.  A NaN reaching the velocity would
 * spread to the position and never leave.
 */
function axis(v) {
  return typeof v === "number" && Number.isFinite(v) ? Math.min(1, Math.max(0, v)) : (v ? 1 : 0);
}

const BEADS = [-HALF_LEN, -HALF_LEN / 2, 0, HALF_LEN / 2, HALF_LEN];

// launch() reads this.vel while it writes it, so the arithmetic lands here
// first rather than aliasing the destination it is still reading from.
const _launch = new THREE.Vector3();

const SKIN = 1e-4;                 // penetration below this is resting, not colliding
const PEN2 = (RADIUS - SKIN) * (RADIUS - SKIN);

/**
 * How the shape is allowed to turn.  `facing` slews at TURN_RATE with no
 * regard for collision -- it always did, and the animation depends on it --
 * but a 0.38 m shape pivoting inside a 0.27 m lane sweeps into both walls at
 * once, and resolving that as ordinary penetration either ejects the cat
 * through a wall or wedges it for good.  So the shape gets its own yaw,
 * `shapeYaw`, which chases `facing` only as far as the world lets it: each
 * frame it advances in substeps, and a substep that leaves the shape
 * overlapping something -- and that a settle of a few centimetres cannot fix
 * -- is refused, leaving the *visual* cat free to turn its head and
 * shoulders while the collision shape waits for room.
 *
 * Two details carry this:
 *
 * - The shape is symmetric fore-and-aft, so shapeYaw only matters modulo pi.
 *   Turning clean around inside a passage -- the move a player actually makes
 *   in a dead end like the bin lane -- needs no shape rotation at all, and
 *   the chase is never more than a quarter turn behind.
 *
 * - Substeps are capped at SHAPE_STEP so a bead end sweeps at most
 *   HALF_LEN * 0.15 = 1 cm per test, far less than the 12 cm of the thinnest
 *   wall; rotation can therefore never step *over* geometry, only be stopped
 *   by it.  The settle exists because pivoting flush against a wall always
 *   grazes it -- a real cat's haunch shoves it off the wall as it turns, and
 *   SETTLE_CAP bounds that shove to centimetres so it can never become an
 *   ejection.
 */
const SHAPE_TURN = 2 * TURN_RATE;  // catch-up is a quarter turn at most
const SHAPE_STEP = 0.15;           // rad per collision-tested substep
const SETTLE_CAP = 0.05;           // metres a pivot may shove the body

// A cat steps onto things at chest height without it reading as a jump, and
// every stair in the level is built to a 0.183-0.217 m riser.  At 0.145 the
// player had to press jump on every single tread, which felt awful.  0.23
// walks all of them and still refuses the 0.26 m ledges meant to need a jump.
const STEP_HEIGHT = 0.23;

/**
 * How far the cat may hang over the surface it is walking on before it counts
 * as having left it.
 *
 * Going downhill the ground falls away by speed * gradient * dt in a frame,
 * while gravity carries the cat down only GRAVITY * dt^2 in the same frame --
 * dt^2 and not half of it, because the integration is semi-implicit, velocity
 * updated before position.  So above a gradient of GRAVITY * dt / speed the
 * ground is gone from under the paws every single frame: 11% at a sprint and
 * 42% at a walk on a 60 Hz display, 4.5% and 17% on a 144 Hz one.  The creek
 * banks in the level are 36%, which puts them the wrong side of that at a
 * sprint on any display and at a walk on a fast one -- and the faster the
 * display, the worse it gets, since a smaller dt lowers the threshold.
 *
 * `grounded` strobing false/true down a bank is not just a flicker in the
 * animation, though that is what you see first -- resolveState reads "fall"
 * off it, so the fall clip beats against walk or run the whole way down.  The
 * cat also accelerates with AIR_ACCEL instead of ACCEL, loses its friction,
 * and cannot stalk or swipe anywhere the ground is not level.
 *
 * The gap that opens in one frame is at most top speed * the steepest ground *
 * the longest frame, so that is what this is rather than a number that looked
 * about right: main.js clamps dt to 50 ms, and 0.5 is spec.js's 36% banks
 * rounded well up so terrain built later does not quietly outrun it.  If the
 * level does grow something steeper, this is arithmetic, not a re-debug.
 */
const MAX_GRADE = 0.5;
const DT_MAX = 0.05;                                  // main.js's frame clamp
const GROUND_SNAP = RUN_TOP * MAX_GRADE * DT_MAX;     // 0.060 m

// The arrival speed that earns the full landing crouch: what the cat is doing
// after falling its own step height.  Anything it could have walked down
// instead is not a landing and should not cost 0.30 s of one.
const LAND_HARD = Math.sqrt(2 * GRAVITY * STEP_HEIGHT);   // 2.67 m/s

export class Player {
  /**
   * `groundY(x, z)` is the height of the ground itself -- the backstop under
   * everything, below which the cat cannot fall.  It is a function because
   * the block is not flat: the gardens behind the houses are a terrace down
   * from the street.  Defaults to the flat world the clearing used.
   */
  constructor(grid, groundY = () => 0) {
    this.grid = grid;
    this.groundY = groundY;
    this.pos = new THREE.Vector3(0, 0, 3.2);   // origin sits at the paws
    this.vel = new THREE.Vector3();
    this.facing = 0;                        // away from the start camera
    this.shapeYaw = 0;                      // where the collision shape points;
                                            // trails facing where walls demand
    this.grounded = true;
    this.groundedFor = 0;
    this.airborneFor = 0;
    this.jumpHeld = 0;
    this.charging = false;
    this.chargeTime = 0;                // s the current charge has been held
    this.lastPower = 1;                 // multiplier of the most recent launch
    this.releaseBuffer = 0;             // see BUFFER: the forgiven event is the
    this.releasePower = 1;              //   release, at the power it held then
    this.releaseAimed = false;          //   and whether it was being steered
    this.aimFor = AIM_GRACE;            // s since a direction was last held.
                                        //   Starts spent: a cat that has never
                                        //   steered is not aiming at anything
    this.airFloor = 0;                  // horizontal speed at take-off; air
                                        //   control may steer it, not brake it
    this.clinging = false;
    this.clingBox = null;               // the collider hung from, and the face
    this.clingNX = 0;                   //   held: its outward normal, axis-
    this.clingNZ = 0;                   //   aligned because the boxes are
    this.wallBox = null;                // most recent horizontal contact this
    this.wallNX = 0;                    //   frame -- moveAxis reports, update
    this.wallNZ = 0;                    //   resets before the horizontal pass
    this.state = "idle";
    this.stateTime = 0;
    this.sitting = false;
    this.stalking = false;
    this.swipeTimer = 0;
    this.landTimer = 0;
    this.burrs = null;                  // burr snare (x,y,z,slack)=>bush|null,
                                        //   injected by main.js and world.js;
                                        //   null (no burrs) for bare harnesses
    this.snagFor = 0;                   // s left tangled in a burr bush
    this.snagSafe = 0;                  // s of post-escape immunity left
    this.snagHeld = false;              // jump level last frame, mash edges
    this.queryBox = new THREE.Box3();
  }

  /** The shape's bounds at any yaw, padded, for asking the broadphase. */
  near() {
    const reach = RADIUS + HALF_LEN;
    this.queryBox.min.set(this.pos.x - reach, this.pos.y, this.pos.z - reach);
    this.queryBox.max.set(this.pos.x + reach, this.pos.y + HEIGHT,
                          this.pos.z + reach);
    this.queryBox.expandByScalar(0.6);
    return this.grid.near(this.queryBox);
  }

  get speed() {
    return Math.hypot(this.vel.x, this.vel.z);
  }

  get charge() {
    return Math.min(1, this.chargeTime / CHARGE_TIME);
  }

  get power() {
    return 1 + (POWER_MAX - 1) * this.charge;
  }

  /**
   * Charging hard enough to show it.  `charging` goes true on the press
   * frame, which is one frame of every tap-jump too, so anything the player
   * can SEE -- the crouch, the speed cap, the meter -- hangs off this instead
   * and appears only once the hold has outlived POSE_DELAY.  One threshold,
   * so the pose, the plant and the HUD can never disagree about whether a
   * pounce is being gathered.
   */
  get gathering() {
    return this.charging && this.chargeTime > POSE_DELAY;
  }

  /**
   * The SKIN tests here are load-bearing, same as they were for the old box.
   * A cat standing on a crate *touches* its top exactly, so counting touch as
   * collision makes the horizontal pass "resolve" against the very surface
   * being stood on -- ejecting the cat sideways off everything it rests on.
   * Requiring real penetration both vertically and in plan is what separates
   * resting on something from being inside it.
   */
  overlapsY(c) {
    return Math.min(this.pos.y + HEIGHT, c.max.y) -
           Math.max(this.pos.y, c.min.y) > SKIN;
  }

  /** Does the shape's plan section penetrate c's footprint by more than SKIN? */
  overlapsXZ(c) {
    const ax = Math.sin(this.shapeYaw), az = Math.cos(this.shapeYaw);
    for (const f of BEADS) {
      const bx = this.pos.x + f * ax, bz = this.pos.z + f * az;
      const dx = bx - Math.min(Math.max(bx, c.min.x), c.max.x);
      const dz = bz - Math.min(Math.max(bz, c.min.z), c.max.z);
      if (dx * dx + dz * dz < PEN2) return true;
    }
    return false;
  }

  /**
   * How far the shape must back out along `axis` (against `dir`) to stop
   * penetrating c.  Per bead it is the old box arithmetic with the box's
   * half-width replaced by the circle's half-width *at the collider's edge*:
   * a bead meeting a face square-on presents its full RADIUS, one catching a
   * corner presents sqrt(R^2 - miss^2), which is exactly what lets the round
   * end slide off a corner the old box slammed into.  Beads that are not
   * penetrating contribute nothing -- the tail must not veto the nose.
   */
  ejectAlong(axis, dir, c) {
    const ax = Math.sin(this.shapeYaw), az = Math.cos(this.shapeYaw);
    let out = 0;
    for (const f of BEADS) {
      const bx = this.pos.x + f * ax, bz = this.pos.z + f * az;
      const dx = bx - Math.min(Math.max(bx, c.min.x), c.max.x);
      const dz = bz - Math.min(Math.max(bz, c.min.z), c.max.z);
      if (dx * dx + dz * dz >= PEN2) continue;
      if (axis === "x") {
        const w = Math.sqrt(RADIUS * RADIUS - dz * dz);
        out = Math.max(out, dir > 0 ? bx + w - c.min.x : c.max.x + w - bx);
      } else {
        const w = Math.sqrt(RADIUS * RADIUS - dx * dx);
        out = Math.max(out, dir > 0 ? bz + w - c.min.z : c.max.z + w - bz);
      }
    }
    return out;
  }

  /**
   * Move along one axis and push back out of anything genuinely overlapped.
   *
   * Resolving axes one at a time is what lets the cat slide along a crate
   * instead of sticking to it, and it keeps every response exact -- circle
   * against face, never a diagonal case to approximate.
   */
  moveAxis(axis, amount) {
    if (amount === 0) return;
    this.pos[axis] += amount;
    // Two passes: pushing out of one box can nudge the cat into a neighbour,
    // which matters where the world is built from abutting blocks.
    for (let pass = 0; pass < 2; pass++) {
      let touched = false;
      // Query once per pass: resolution only ever pushes the cat out of a box
      // already in this set, and a second pass re-queries for anything it was
      // pushed into.
      const candidates = this.near();
      for (const c of candidates) {
        if (!this.overlapsY(c)) continue;
        if (axis === "y") {
          if (!this.overlapsXZ(c)) continue;
          if (amount > 0) this.pos.y -= this.pos.y + HEIGHT - c.min.y;
          else this.pos.y += c.max.y - this.pos.y;
          this.vel.y = 0;
          touched = true;
          continue;
        }
        const e = this.ejectAlong(axis, amount > 0 ? 1 : -1, c);
        if (e <= 0) continue;
        this.pos[axis] -= Math.sign(amount) * e;
        touched = true;
        // Contact report: which box stopped the motion and which face it
        // presented, for the claws.  Fields on the player, not a result
        // object -- this runs several times a frame and must not allocate.
        // A climbable box wins over whatever was recorded before it, so a
        // cat jammed into the corner between a fence and a house clings to
        // the fence rather than being refused for touching the brick.
        if (!this.wallBox || c.climb) {
          this.wallBox = c;
          this.wallNX = axis === "x" ? -Math.sign(amount) : 0;
          this.wallNZ = axis === "z" ? -Math.sign(amount) : 0;
        }
      }
      if (!touched) break;
    }
  }

  overlapsAny() {
    const candidates = this.near();
    for (const c of candidates) {
      if (this.overlapsY(c) && this.overlapsXZ(c)) return true;
    }
    return false;
  }

  /**
   * Advance the collision shape's yaw toward `facing`, but only through
   * orientations the world has room for.  See the SHAPE_TURN comment for why
   * this exists at all.  Modulo-pi first: the shape is symmetric, so a
   * quarter turn is the farthest it ever has to chase.  Each substep is
   * accepted as-is, accepted with a small settle (pivoting flush to a wall
   * always grazes it), or refused wholesale -- position and yaw restored --
   * so a blocked turn leaves the cat exactly where and how it stood.
   */
  turnShape(dt) {
    let d = (this.facing - this.shapeYaw) % Math.PI;
    if (d > Math.PI / 2) d -= Math.PI;
    else if (d <= -Math.PI / 2) d += Math.PI;
    if (Math.abs(d) < 1e-4) { this.shapeYaw += d; return; }
    let want = Math.min(Math.abs(d), SHAPE_TURN * dt);
    const dir = Math.sign(d);
    while (want > 1e-5) {
      const step = Math.min(want, SHAPE_STEP);
      const yaw = this.shapeYaw, px = this.pos.x, pz = this.pos.z;
      this.shapeYaw += dir * step;
      if (this.overlapsAny() && !this.settleXZ(SETTLE_CAP)) {
        this.shapeYaw = yaw;
        this.pos.x = px;
        this.pos.z = pz;
        return;
      }
      want -= step;
    }
  }

  /**
   * Nudge the body horizontally out of shallow penetration, deepest contact
   * first, giving up rather than shoving farther than `cap`.  Only turnShape
   * calls this: rotation has no motion axis for moveAxis's directed ejection,
   * but it also only ever creates centimetre-deep grazes, so pushing each
   * bead out along its own contact normal converges in a couple of rounds.
   * Between two walls the rounds fight each other, the budget runs out, and
   * the caller reverts the rotation -- which is the correct answer in a lane
   * too narrow to turn in, and the reason this can never eject the cat.
   */
  settleXZ(cap) {
    let moved = 0;
    for (let iter = 0; iter < 4; iter++) {
      const ax = Math.sin(this.shapeYaw), az = Math.cos(this.shapeYaw);
      let worst = SKIN, nx = 0, nz = 0;
      for (const c of this.near()) {
        if (!this.overlapsY(c)) continue;
        for (const f of BEADS) {
          const bx = this.pos.x + f * ax, bz = this.pos.z + f * az;
          const cx = Math.min(Math.max(bx, c.min.x), c.max.x);
          const cz = Math.min(Math.max(bz, c.min.z), c.max.z);
          const dx = bx - cx, dz = bz - cz;
          const dist = Math.hypot(dx, dz);
          if (dist >= RADIUS) continue;
          let pen, px, pz;
          if (dist > 1e-9) {
            pen = RADIUS - dist; px = dx / dist; pz = dz / dist;
          } else {
            // Bead centre inside the box -- deeper than rotation alone can
            // manage, but be exact anyway: out through the nearest face.
            const ex0 = bx - c.min.x, ex1 = c.max.x - bx;
            const ez0 = bz - c.min.z, ez1 = c.max.z - bz;
            const m = Math.min(ex0, ex1, ez0, ez1);
            pen = RADIUS + m;
            px = m === ex0 ? -1 : m === ex1 ? 1 : 0;
            pz = px !== 0 ? 0 : m === ez0 ? -1 : 1;
          }
          if (pen > worst) { worst = pen; nx = px; nz = pz; }
        }
      }
      if (worst <= SKIN) return true;
      moved += worst;
      if (moved > cap) return false;
      this.pos.x += nx * worst;
      this.pos.z += nz * worst;
    }
    return !this.overlapsAny();
  }

  /**
   * The highest surface within `depth` under the paws, or null for thin air.
   *
   * Deliberately a query and not a move.  The obvious way to probe downward is
   * moveAxis("y", -depth) and a look at where it stopped, but moveAxis resolves
   * an overlap by ejecting along its axis, so a probe that starts inside
   * anything at all comes back having teleported the cat to the top of it --
   * and the whole point of the probe is to run in the frames where the cat's
   * footing is uncertain.
   *
   * The ground plane is one candidate among the collider tops rather than a
   * special case, because a cat walking down a flight of stairs or a run of
   * stepping stones set into a bank is losing its footing for exactly the same
   * reason as one walking down the bank itself.
   */
  supportBelow(depth) {
    const candidates = this.near();
    let best = null;
    for (const c of candidates) {
      if (!this.overlapsXZ(c)) continue;
      // Only tops at or below the paws: a box whose lid is above them is
      // something the cat is beside or inside, not standing on.
      if (c.max.y > this.pos.y + SKIN || c.max.y < this.pos.y - depth) continue;
      if (best === null || c.max.y > best) best = c.max.y;
    }
    const g = this.groundY(this.pos.x, this.pos.z);
    if (g <= this.pos.y + SKIN && g >= this.pos.y - depth &&
        (best === null || g > best)) best = g;
    return best;
  }

  /**
   * The ground's surface normal, by central difference on groundY.
   *
   * Nothing in the player reads this yet.  The cat floors against groundY but
   * is never tilted by it -- main.js sets rotation.y alone, so it stands bolt
   * upright on a 36% bank -- and whatever eventually leans it into a hill
   * belongs on the far side of that.  It lives here because the slope is the
   * player's business: groundY is the function the player was handed, and a
   * renderer sampling terrain of its own is how the cat ends up leaning
   * against a hill it is not standing on.
   *
   * `h` is a baseline, not an epsilon: it wants to read the shape of the
   * ground rather than the millimetre under the paws, and at 0.1 m it is still
   * a small fraction of the narrowest thing in the terrain (the creek is 3.5 m
   * bank to bank).
   */
  groundNormal(x = this.pos.x, z = this.pos.z, h = 0.1) {
    const gx = (this.groundY(x + h, z) - this.groundY(x - h, z)) / (2 * h);
    const gz = (this.groundY(x, z + h) - this.groundY(x, z - h)) / (2 * h);
    return new THREE.Vector3(-gx, 1, -gz).normalize();
  }

  /**
   * Lift, move, and settle -- the standard step-up.
   *
   * Every branch that fails restores the original position, so a blocked cat
   * is never left hovering at step height in front of a wall it could not
   * actually climb.
   */
  tryStep(dx, dz) {
    const sx = this.pos.x, sy = this.pos.y, sz = this.pos.z;
    const sv = this.vel.y;

    this.pos.y += STEP_HEIGHT;
    if (this.overlapsAny()) {                    // no headroom up there
      this.pos.set(sx, sy, sz);
      return false;
    }
    this.moveAxis("x", dx);
    this.moveAxis("z", dz);
    if (Math.hypot(this.pos.x - sx, this.pos.z - sz) < 1e-4) {
      this.pos.set(sx, sy, sz);
      this.vel.y = sv;
      return false;
    }

    const lifted = this.pos.y;
    this.moveAxis("y", -STEP_HEIGHT);
    this.vel.y = sv;
    if (this.pos.y <= lifted - STEP_HEIGHT + 1e-6) {   // nothing under the step
      this.pos.set(sx, sy, sz);
      return false;
    }
    return true;
  }

  /**
   * The velocity a launch at `power` would leave with, from the velocity the
   * cat carries right now.  `out` may be any Vector3; nothing here is mutated.
   *
   * The lunge is the charge spent sideways.  It goes along `facing` rather
   * than along the input, because facing is what the crouched cat is visibly
   * pointing at -- and it is spent only when the player is actually steering,
   * so a charge held with no direction is still a pure vertical pounce and
   * the ledge directly overhead stays reachable.
   *
   * Split out of launch() for pounce.js.  The aim preview integrates a whole
   * flight forward to draw where the cat will land, and it has to start from
   * the *same* vector the real launch will use: a second copy of this
   * arithmetic is a preview that can quietly drift out of step with the leap
   * it promises, and an aiming line that lies is worse than no line at all.
   */
/** @param {number} power @param {boolean} aimed @param {number} yaw */
  launchVelocity(power, aimed, out = new THREE.Vector3()) {
    out.set(this.vel.x, Math.sqrt(2 * GRAVITY * JUMP_HEIGHT * power), this.vel.z);
    const charge = (power - 1) / (POWER_MAX - 1);
    if (aimed && charge > 0) {
      const lunge = POUNCE_LUNGE * charge;
      out.x += Math.sin(this.facing) * lunge;
      out.z += Math.cos(this.facing) * lunge;
    }
    return out;
  }

  /**
   * Is a release this frame going to spend the charge as a lunge?
   *
   * The one answer both the launch and the preview read, so the arc drawn on
   * the ground and the leap that follows it can never disagree about whether
   * the cat is aiming or pouncing straight up.  See AIM_GRACE.
   */
  get aiming() {
    return this.aimFor < AIM_GRACE;
  }

  /**
   * Leave the ground.  `power` multiplies the base jump *energy*, so the apex
   * is JUMP_HEIGHT * power exactly -- height is linear in launch energy.
   * Spending the coyote grace here is what keeps the take-off frame from
   * financing a second launch.
   */
  launch(power, aimed) {
    this.vel.copy(this.launchVelocity(power, aimed, _launch));
    // What leaves the ground stays leaving the ground: see AIR_FLOOR.
    this.airFloor = Math.max(WALK_TOP, Math.hypot(this.vel.x, this.vel.z));
    this.grounded = false;
    this.airborneFor = COYOTE;
    this.sitting = false;
    this.charging = false;
    this.chargeTime = 0;
    this.releaseBuffer = 0;
    this.lastPower = power;
    this.setState("jump");
  }

  /**
   * One frame spent hanging on the claws.
   *
   * The order matters: the wall jump goes first because a press is a decision
   * already made, and letting go is checked before the scrabble so a frame
   * that both releases the claws and holds W cannot climb on its way out.
   * The scrabble runs through moveAxis, so clawing up under the lintel over
   * the board gap stops against it the same way walking into it would.
   */
  updateCling(dt, input) {
    const held = !!input.jump;
    const pressed = held && this.jumpHeld === 0;
    this.jumpHeld = held ? this.jumpHeld + dt : 0;
    this.airborneFor += dt;      // hanging is not standing: a later release
    this.groundedFor = 0;        //   gets no coyote grace off the wall
    this.landTimer = 0;
    this.swipeTimer = Math.max(0, this.swipeTimer - dt);
    this.stalking = false;

    // A hanging cat faces what it hangs from.  Eased at TURN_RATE rather
    // than snapped: a claw catch from a glancing leap can be most of a
    // quarter turn, and shapeYaw could not follow a snap through a wall.
    this.facing = turnToward(this.facing,
                             Math.atan2(-this.clingNX, -this.clingNZ),
                             TURN_RATE * dt);
    this.turnShape(dt);

    if (pressed) {
      this.wallJump();
    } else if (!input.claw) {
      this.clinging = false;                     // claws up: let go, drop
      this.clingBox = null;
    } else {
      // Scrabbling stays decisive rather than analog: hanging by the claws
      // is a committed act, and a half-hearted stick reading would leave the
      // cat inching up a fence at a rate that reads as being stuck.
      const want = (axis(input.forward) > 0.5 ? 1 : 0) - (axis(input.back) > 0.5 ? 1 : 0);
      let dy = want * CLING_CLIMB * dt;
      // The top hold: the claws must keep wall to hook into (CLAW_HOOK), so
      // the scrabble parks 0.28 m under the cap and the wall jump does the
      // rest.  Downward needs no clamp -- the checks below catch arrivals.
      const top = this.clingBox.max.y - CLAW_HOOK;
      if (dy > 0 && this.pos.y + dy > top) dy = Math.max(0, top - this.pos.y);
      if (dy !== 0) {
        const before = this.pos.y;
        this.moveAxis("y", dy);
        if (dy < 0 && this.pos.y > before + dy + 1e-9) {
          this.clinging = false;                 // toes met something solid:
          this.clingBox = null;                  //   step off onto it
          this.grounded = true;
        }
      }
      const floor = this.groundY(this.pos.x, this.pos.z);
      if (this.pos.y <= floor) {
        this.pos.y = floor;
        this.clinging = false;                   // scrabbled down to earth
        this.clingBox = null;
        this.grounded = true;
      } else if (this.clinging &&
                 this.pos.y + CLAW_HOOK < this.clingBox.min.y) {
        this.clinging = false;                   // below the boards (the dug
        this.clingBox = null;                    //   gap's raised bay): air
      }
    }

    if (this.grounded) { this.groundedFor = dt; this.airborneFor = 0; }
    this.stateTime += dt;
    return this.resolveState();
  }

  /**
   * Space from a cling: an uncharged jump straight up the face.
   *
   * Deliberately no push away from the wall.  The wall jump exists to
   * ratchet -- cling, tap, catch again higher -- and any outward component
   * is drift the player has to steer back before the claws can re-catch:
   * measured at 0.4 m/s of kick-off, a neutral leap is 0.33 m off the face
   * by its apex, and even held-W recovery re-catches a quarter second later
   * and 0.27 m lower than the flush leap does.  Straight up, the cat rises
   * alongside the face it is holding and one frame of W re-presses it.
   * Getting OFF the wall is what the claws-up release is for, or steering
   * away during the leap.
   */
  wallJump() {
    this.clinging = false;
    this.clingBox = null;
    this.vel.set(0, Math.sqrt(2 * GRAVITY * JUMP_HEIGHT), 0);
    this.airFloor = WALK_TOP;    // nothing horizontal left the wall; the
    this.grounded = false;       //   floor only keeps steering un-braked
    this.airborneFor = COYOTE;   // spent, exactly as launch spends it
    this.lastPower = 1;
    this.setState("jump");
  }

  /**
   * The narrow-top nudge -- the ASSIST_* block above is the contract.
   *
   * Runs only while airborne and descending.  Of everything narrow whose top
   * is just below the paws, take the one the cat is closest to actually
   * catching, and pull toward the nearest point of its footprint.  The pull
   * goes through moveAxis, so it resolves like any other motion and can
   * never press the capsule into a neighbouring box.
   */
  landAssist(dt) {
    let ox = 0, oz = 0;
    let best = RADIUS + ASSIST_MARGIN;     // farther than this is a plain miss
    for (const c of this.near()) {
      if (Math.min(c.max.x - c.min.x, c.max.z - c.min.z) > ASSIST_TOP) continue;
      if (c.max.y > this.pos.y || this.pos.y - c.max.y > ASSIST_ABOVE) continue;
      const dx = Math.min(Math.max(this.pos.x, c.min.x), c.max.x) - this.pos.x;
      const dz = Math.min(Math.max(this.pos.z, c.min.z), c.max.z) - this.pos.z;
      const d = Math.hypot(dx, dz);
      if (d < 1e-6 || d >= best) continue;     // already over it, or farther
      best = d;
      ox = dx;
      oz = dz;
    }
    if (ox === 0 && oz === 0) return;
    const step = Math.min(ASSIST_PULL * dt, best);
    this.moveAxis("x", (ox / best) * step);
    this.moveAxis("z", (oz / best) * step);
  }

  update(dt, input, camYaw) {
    // A clung cat is parked on its claws: no gravity, no gait, no charge.
    // The whole normal flow waits until something below lets go.
    if (this.clinging) return this.updateCling(dt, input);

    // ---- the burr tangle's clock (the SNAG_GRIP block explains the rules) --
    if (this.snagFor > 0) {
      if (this.burrs && !this.burrs(this.pos.x, this.pos.y, this.pos.z, SNAG_GRIP)) {
        this.snagFor = 0;              // teleported out from under the bush
      } else {
        // The wriggle: a fresh press of jump -- edge, never level, or a held
        // Space would count as sixty presses a second -- tears a bite off.
        if (input.jump && !this.snagHeld) {
          this.snagFor = Math.max(0, this.snagFor - SNAG_MASH);
        }
        this.snagFor -= dt;            // unconditional: the tangle ALWAYS ends
        if (this.snagFor <= 0) {
          this.snagFor = 0;
          this.snagSafe = SNAG_IMMUNE; // walk clear before it can re-arm
        }
      }
    } else if (this.snagSafe > 0) {
      this.snagSafe = Math.max(0, this.snagSafe - dt);
    }
    this.snagHeld = !!input.jump;
    const snagged = this.snagFor > 0;

    // ---- desired horizontal velocity, in camera space --------------------
    // `axis` reads each direction as a MAGNITUDE, not a flag, so a thumbstick
    // can ask for a fraction of a gait.  A key writes exactly 1 or 0, so
    // nothing about keyboard play changes; what it buys is a phone, where a
    // stick that only ever said "full tilt or nothing" would be a D-pad with
    // extra steps.  The value flows straight into the target velocity below,
    // so half a stick is half the speed of whatever gait is selected -- and
    // it is what lets a cat creep, which is most of playing one.
    let mf = axis(input.forward) - axis(input.back);
    let mr = axis(input.right) - axis(input.left);
    const mag = Math.hypot(mf, mr);
    if (mag > 1) { mf /= mag; mr /= mag; }

    // camYaw places the camera at (sin, cos) *from* the cat, so "away from the
    // camera" -- what W has to mean at every yaw -- is the negative of that,
    // and screen-right is that forward turned -90 degrees about Y.
    const sin = Math.sin(camYaw), cos = Math.cos(camYaw);
    const fwdX = -sin, fwdZ = -cos;
    const rightX = cos, rightZ = -sin;
    const wantX = mf * fwdX + mr * rightX;
    const wantZ = mf * fwdZ + mr * rightZ;
    // Past POSE_DELAY a hold is a pounce being gathered, and the run has to
    // die the frame the crouch shows -- the pose and the physics must not
    // disagree about whether the cat is running.  Under it a tap keeps full
    // speed into the launch, which keeps a running jump a running jump.
    const gathering = this.gathering;
    const top = gathering ? CHARGE_TOP
              : this.stalking ? STALK_TOP : input.sprint ? RUN_TOP : WALK_TOP;
    // Air control eases toward the target from EITHER side, so in the air a
    // top speed below what the cat launched at is a brake: holding W through
    // a pounce used to drag 2.8 m/s back down to WALK_TOP and cost most of
    // the leap.  Airborne, the launch speed is a floor under the target --
    // steering redirects the arc, it never scrubs it.  Grounded is untouched,
    // so nothing here can make the cat run faster than its gait allows.
    const cap = this.grounded ? top : Math.max(top, this.airFloor);
    const targetX = wantX * cap;
    const targetZ = wantZ * cap;

    const moving = mag > 0.01;
    if (moving) this.sitting = false;
    // The aim clock, ticked before anything can read `aiming`: while a
    // direction is held the aim is live and pointed at `facing`, and for
    // AIM_GRACE after it comes up the last aim still stands.  Ticked every
    // frame rather than only while charging so the preview, which is drawn
    // from the same getter, has an answer on the frame the charge begins.
    if (moving) this.aimFor = 0;
    else this.aimFor = Math.min(AIM_GRACE, this.aimFor + dt);
    const a = (this.grounded ? ACCEL : AIR_ACCEL) * dt;
    if (moving) {
      this.vel.x += (targetX - this.vel.x) * Math.min(1, a);
      this.vel.z += (targetZ - this.vel.z) * Math.min(1, a);
      this.facing = turnToward(this.facing, Math.atan2(wantX, wantZ),
                               TURN_RATE * dt);
    } else if (this.grounded) {
      const drop = Math.max(0, 1 - FRICTION * dt);
      this.vel.x *= drop;
      this.vel.z *= drop;
    }

    if (gathering && this.speed > CHARGE_TOP) {
      // The plant is a hard clamp, not an ACCEL ease: eased at 14/s a sprint
      // spends the first quarter second of a 0.62 s charge still sliding at
      // a run.  A cat gathering a pounce brakes with its whole body at once.
      const s = CHARGE_TOP / this.speed;
      this.vel.x *= s;
      this.vel.z *= s;
    }

    // Tangled in burrs: the same hard clamp as the pounce plant, for the
    // same reason -- burrs hold the whole body at once, and easing into the
    // hold would spend the first strides of every tangle sliding free at a
    // run.  A clamp rather than zero because a frozen cat under a live
    // stick is the failure mode this whole feature is designed against:
    // the input still visibly drags the body its strain's worth.
    if (snagged && this.speed > SNAG_CREEP) {
      const s = SNAG_CREEP / this.speed;
      this.vel.x *= s;
      this.vel.z *= s;
    }

    // Chase facing with the collision shape every frame, moving or not --
    // a turn refused in a pinch has to be retried once the cat steps clear,
    // and that step may happen frames after the input that wanted the turn.
    this.turnShape(dt);

    // ---- the pounce: charge on hold, launch on release -------------------
    // Tangled, the jump key is the wriggle (spent above) and nothing else:
    // masking the level here is what keeps every consumer honest at once --
    // no charge gathers, no launch fires, and a release remembered from
    // before the tangle stays spent (entry zeroed the buffer; the masked
    // level cannot refill it).  A press still DOWN at the moment of escape
    // re-registers as an ordinary grounded hold, so the last mash of a
    // successful wriggle often ends in a small hop out of the bush -- kept,
    // deliberately: it launches at tap power under the immunity, reads as
    // bursting free, and suppressing it would need the key to go up before
    // it worked again, which is exactly the dead-controller feel this
    // feature is designed against.
    const held = !!input.jump && !snagged;
    const pressed = held && this.jumpHeld === 0;
    const released = !held && this.jumpHeld > 0;
    this.jumpHeld = held ? this.jumpHeld + dt : 0;

    // A release remembered from just above the ground fires here, the frame
    // after touchdown -- the frame the old press buffer fired on, so moving
    // the forgiveness from the press to the release changes which event is
    // forgiven and nothing about when the cat leaves.
    if (this.releaseBuffer > 0 && this.grounded && !snagged && this.vel.y <= 0.01) {
      this.launch(this.releasePower, this.releaseAimed);
    }
    this.releaseBuffer = Math.max(0, this.releaseBuffer - dt);

    if (this.charging) {
      if (held && this.grounded) this.chargeTime += dt;
      else if (!this.grounded && this.airborneFor >= COYOTE) {
        // Crept off an edge mid-charge and outstayed the coyote grace.  The
        // pounce was gathered against ground that is no longer there, so it
        // is forfeit and the hold is now just a key down during a fall.
        this.charging = false;
        this.chargeTime = 0;
      }
    }
    // A charge begins on a grounded press, and also on touching down with the
    // key already held: a pounce is gathered against the ground, so a hold
    // that spanned any air starts over from zero.  The begin frame contributes
    // no time -- that is what makes a one-frame tap launch at exactly 1.0.
    if (!this.charging && held &&
        (this.grounded || (pressed && this.airborneFor < COYOTE))) {
      this.charging = true;
      this.chargeTime = 0;
      this.sitting = false;
    }

    if (released) {
      // The vel.y guard is the old take-off guard: a launch must never fire
      // into a frame already moving upward.
      if ((this.grounded || this.airborneFor < COYOTE) && this.vel.y <= 0.01) {
        this.launch(this.power, this.aiming);
      } else if (!this.grounded) {
        this.releaseBuffer = BUFFER;
        this.releasePower = this.power;
        this.releaseAimed = this.aiming;
      }
      this.charging = false;
      this.chargeTime = 0;
    }

    this.vel.y -= GRAVITY * dt;

    // ---- integrate and resolve ------------------------------------------
    const wasGrounded = this.grounded;
    const dx = this.vel.x * dt, dz = this.vel.z * dt;
    const fromX = this.pos.x, fromZ = this.pos.z;
    this.wallBox = null;               // contacts are per-frame news
    this.moveAxis("x", dx);
    this.moveAxis("z", dz);

    // If a low lip stopped us, try mounting it.  Without this a cat is halted
    // dead by a 16 cm plank, which reads as the world being sticky rather than
    // as an obstacle worth jumping.
    const wanted = Math.hypot(dx, dz);
    const got = Math.hypot(this.pos.x - fromX, this.pos.z - fromZ);
    if (wasGrounded && wanted > 1e-5 && got < wanted * 0.7) {
      this.pos.x = fromX;
      this.pos.z = fromZ;
      if (!this.tryStep(dx, dz)) {
        this.moveAxis("x", dx);
        this.moveAxis("z", dz);
      }
    }

    // ---- the claws catch ------------------------------------------------
    // Airborne, claws out, pressed into a climbable face, and near the top
    // of the arc (see CLING_CATCH): hook on.  The CLAW_HOOK tests are the
    // purchase -- the wall has to reach past where the claws hold, so a cat
    // whose head is already above a fence cap is refused here and handed to
    // the landing assist instead, which is the tool for that situation.
    if (input.claw && !this.grounded && this.vel.y <= CLING_CATCH &&
        this.wallBox && this.wallBox.climb &&
        this.pos.y + CLAW_HOOK <= this.wallBox.max.y &&
        this.pos.y + CLAW_HOOK >= this.wallBox.min.y) {
      this.clinging = true;
      this.clingBox = this.wallBox;
      this.clingNX = this.wallNX;
      this.clingNZ = this.wallNZ;
      this.vel.set(0, 0, 0);
      this.charging = false;
      this.chargeTime = 0;
      this.releaseBuffer = 0;      // a release remembered from before the
      this.sitting = false;        //   grab must not fire on the next ground
      this.airborneFor += dt;
      this.groundedFor = 0;
      this.stateTime += dt;
      return this.resolveState();  // the fall ends here: skip gravity's move
    }

    if (!this.grounded && this.vel.y < 0) this.landAssist(dt);

    // Capture the intended step before moving: moveAxis zeroes vel.y on
    // contact, so reading vel.y afterwards reports no fall happened and the
    // cat never registers as standing on anything but the ground plane.
    const dy = this.vel.y * dt;
    const impact = -this.vel.y;       // speed carried into whatever we land on
    const beforeY = this.pos.y;
    this.moveAxis("y", dy);
    const pushedUp = this.pos.y - (beforeY + dy);
    this.grounded = dy <= 0 && pushedUp > 1e-9;

    const floor = this.groundY(this.pos.x, this.pos.z);
    if (this.pos.y <= floor) {        // the ground plane itself
      this.pos.y = floor;
      if (this.vel.y < 0) this.vel.y = 0;
      this.grounded = true;
    } else if (wasGrounded && !this.grounded && this.vel.y <= 0) {
      // Left hanging over ground that fell away faster than gravity: see
      // GROUND_SNAP.  The two guards are what keep this from being a cheat.
      // `vel.y <= 0` means a jump can never be swallowed -- the take-off frame
      // is moving up at 5.4 m/s and clears `wasGrounded` besides.  Requiring
      // something to actually be there within GROUND_SNAP means a real ledge
      // still drops the cat: step off the porch and the probe finds nothing
      // for half a metre, the cat goes airborne, and coyote time covers the
      // edge exactly as it did before.
      const support = this.supportBelow(GROUND_SNAP);
      if (support !== null) {
        this.pos.y = support;
        this.vel.y = 0;
        this.grounded = true;
      }
    }

    if (this.grounded) { this.groundedFor += dt; this.airborneFor = 0;
                        this.airFloor = 0; }
    else { this.airborneFor += dt; this.groundedFor = 0; }

    if (this.grounded && !wasGrounded) {
      // Scale the crouch by how hard the cat actually arrived.  Every arrival
      // used to cost a flat 0.30 s of "land", which is right off the porch
      // roof and absurd for the two centimetres of air the cat takes when a
      // paving slab set into a bank sits a hair proud of the one before it.
      // Under LAND_HARD it is a step down, not a landing, and below 0.04 s
      // resolveState's gate drops the pose entirely.
      this.landTimer = 0.30 * Math.min(1, impact / LAND_HARD);
      if (this.landTimer > 0.04) this.setState("land");
    }
    this.landTimer = Math.max(0, this.landTimer - dt);

    // ---- did the paws just land in a burr bush? --------------------------
    // Checked at the END of the frame, on resolved footing, so the tangle
    // starts exactly where the cat actually stands.  Grounded only: a leap
    // OVER a bush must clear it (the snare's own height band already lets an
    // honest arc through; see BURR_Y_UP), and a cat falling past one is not
    // standing in it.  Entry forfeits any gathered charge -- the pounce was
    // being gathered against legs that are now full of burrs -- and spends
    // any remembered release, so the tangle cannot end in a surprise leap.
    if (!snagged && this.snagSafe <= 0 && this.grounded && this.burrs &&
        this.burrs(this.pos.x, this.pos.y, this.pos.z)) {
      this.snagFor = SNAG_TIME;
      this.charging = false;
      this.chargeTime = 0;
      this.releaseBuffer = 0;
      this.sitting = false;
      this.snagHeld = !!input.jump;    // a key already down is not a wriggle
    }

    if (input.sitToggle && this.grounded && !this.charging &&
        this.speed < 0.05) {
      this.sitting = !this.sitting;
    }

    // Stalking is a held modifier: it is a way of moving, not a mode to be in,
    // so it ends the moment the key comes up rather than needing a toggle off.
    this.stalking = !!input.stalk && this.grounded && !this.charging;
    if (this.stalking) this.sitting = false;

    // The swipe is a one-shot that runs to completion.  Keeping it on a timer
    // rather than a state flag is what lets it play over a stalk and hand the
    // cat back to stalking afterwards instead of dumping it into idle.
    if (input.swipe && this.grounded && this.swipeTimer <= 0) this.swipeTimer = SWIPE_TIME;
    this.swipeTimer = Math.max(0, this.swipeTimer - dt);

    this.stateTime += dt;
    return this.resolveState();
  }

  setState(s) {
    if (this.state !== s) { this.state = s; this.stateTime = 0; }
  }

  resolveState() {
    if (this.clinging) { this.setState("cling"); return this.state; }
    if (this.grounded && this.swipeTimer > 0) { this.setState("swipe"); return this.state; }
    // The tangle's wriggle: bob between the land crouch and idle on the
    // SNAG_BOB cycle.  Two clips every rig already ships, not a new state --
    // burrs.js's SNAG_BOB comment carries the argument (every client on the
    // street, this build or an older one, renders the struggle without ever
    // hearing the word "snag"), and the phase runs off snagFor itself, so a
    // mash that eats the clock visibly redoubles the wriggling.  A swipe
    // still wins above: clawing at the burrs is exactly what it looks like.
    if (this.snagFor > 0 && this.grounded) {
      const phase = this.snagFor % SNAG_BOB;
      this.setState(phase > SNAG_BOB - SNAG_BOB_DOWN ? "land" : "idle");
      return this.state;
    }
    if (!this.grounded) {
      // The launch pose plays out, then the airborne loop takes over.
      if (this.state === "jump" && this.stateTime < 0.26) this.setState("jump");
      else this.setState("fall");
    } else if (this.gathering) {
      // Above land on purpose: the pounce pose is the land crouch held, so a
      // charge begun on touchdown reads as one continuous gather.  Below
      // POSE_DELAY the hold is still presumed a tap, and a tap must not
      // flash a frame of crouch over a plain jump.
      this.setState("pounce");
    } else if (this.landTimer > 0.04 && this.speed < 1.2) {
      this.setState("land");
    } else if (this.stalking) {
      this.setState("stalk");
    } else if (this.speed > 0.06) {
      this.setState(this.speed > GAIT_SWAP ? "run" : "walk");
    } else {
      this.setState(this.sitting ? "sit" : "idle");
    }
    return this.state;
  }
}

function turnToward(from, to, maxStep) {
  let d = ((to - from + Math.PI) % (Math.PI * 2)) - Math.PI;
  if (d < -Math.PI) d += Math.PI * 2;
  return from + THREE.MathUtils.clamp(d, -maxStep, maxStep);
}
